import { getAvatarColor, getInitial } from "@/lib/color/avatar-initials";
import type { ImpersonatableUser } from "../../_logic/db";
import Image from "next/image";
import { memo, useState } from "react";

export const ImpersonateUserRow = memo(function ImpersonateUserRow({
  user,
  handleImpersonate,
}: {
  user: ImpersonatableUser;
  handleImpersonate: (user: ImpersonatableUser) => void;
}) {
  const [imageError, setImageError] = useState(false);

  // Keyed on the same name the row displays (full_name, falling back to first_name) so the initial
  // and color always match what's rendered — first_name alone could disagree with full_name.
  const displayName = user.full_name || user.first_name;
  const initial = getInitial(displayName, user.email);
  const avatarColor = getAvatarColor(displayName, user.email);

  return (
    <button
      key={user.user_id}
      onClick={() => handleImpersonate(user)}
      className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-gray-50 transition-colors border border-gray-200 text-left"
    >
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${avatarColor}`}
      >
        {user.profile_picture_url && user.profile_picture_url.startsWith("http") && !imageError ? (
          <Image
            src={user.profile_picture_url}
            alt={user.full_name ?? `${user.first_name} ${user.last_name}`}
            width={40}
            height={40}
            className="rounded-full object-cover"
            loading="lazy"
            quality={65}
            sizes="40px"
            onError={() => setImageError(true)}
          />
        ) : (
          <span aria-hidden="true" className="text-sm font-medium text-gray-900">
            {initial}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {user.full_name ?? user.first_name + " " + user.last_name}
        </p>
        <p className="text-xs font-medium text-gray-500 truncate">{user.email}</p>
      </div>
    </button>
  );
});
